import { Link } from "react-router-dom";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { useT, useImg } from "@/context/ContentContext";
import { fileUrl } from "@/lib/api";
import { HERO_IMG } from "./HomeData";

export default function HomeHero() {
    const { settings, whatsappLink } = useSettings();
    const t = useT();
    const img = useImg();
    const heroImg = img("home.hero.image", settings.hero_image_id ? fileUrl(settings.hero_image_id) : HERO_IMG);

    return (
        <section className="relative pt-24 pb-8 sm:pb-14">
            <div className="max-w-[1400px] mx-auto px-3 sm:px-4">
                <div className="relative rounded-[2rem] overflow-hidden min-h-[85vh] bg-[hsl(var(--forest))]">
                    <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-55" />
                    <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--forest))]/50 via-[hsl(var(--forest))]/40 to-[hsl(var(--forest))]/85" />

                    <div className="relative h-full min-h-[85vh] flex flex-col justify-between p-6 sm:p-10 lg:p-16">
                        <div />
                        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8 items-start pt-10">
                            <div>
                                <div className="eyebrow-light mb-6"><span className="logo-mark text-sm">H<span className="dot">.</span></span> {t("home.hero.eyebrow", "O Homem da Obra")}</div>
                                <h1 className="font-display text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold leading-[0.98] tracking-tight" data-testid="hero-headline">
                                    {t("home.hero.headline_1", "Obras sem")}<br />
                                    <span className="font-serif-italic text-[hsl(var(--stone))]">{t("home.hero.headline_2", "dores de cabeça")}</span><span className="text-[hsl(var(--copper))]">.</span>
                                </h1>
                                <p className="text-white/85 text-lg leading-relaxed mt-8 max-w-xl" data-testid="hero-subheadline">
                                    {t("home.hero.subheadline", "Remodelações · Gestão de Obra · Valorização de Imóveis. Com método, coordenação e responsabilidade — do orçamento à entrega.")}
                                </p>
                                <div className="flex flex-wrap gap-3 mt-10">
                                    <Link to="/orcamento" data-testid="hero-pedir-orcamento-btn" className="btn-copper">
                                        {t("home.hero.cta", "Pedir orçamento")}
                                        <span className="arrow-circle"><ArrowUpRight className="w-3.5 h-3.5" /></span>
                                    </Link>
                                    <Link to="/como-trabalhamos" className="bg-white/10 backdrop-blur border border-white/30 text-white px-6 py-3.5 rounded-full font-medium inline-flex items-center gap-2 hover:bg-white/20 transition">
                                        {t("home.hero.secondary_cta", "Como trabalhamos")}
                                    </Link>
                                </div>
                            </div>

                            <div className="hidden lg:block">
                                <div className="bg-white/10 backdrop-blur-md border border-white/25 rounded-3xl p-7">
                                    <div className="text-white/90 font-mono uppercase tracking-widest text-xs mb-3">{t("home.hero.aside_eyebrow", "Excelência na execução")}</div>
                                    <div className="font-display text-white text-2xl xl:text-3xl font-bold leading-tight tracking-tight">
                                        {t("home.hero.aside_line1", "Verdade na relação.")}<br />{t("home.hero.aside_line2", "Tranquilidade para si.")}
                                    </div>
                                    <div className="mt-6 pt-5 border-t border-white/20">
                                        <div className="text-white/70 text-xs mb-2">{t("home.hero.aside_cta_label", "Fale connosco")}</div>
                                        <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-[hsl(var(--copper))] hover:bg-[hsl(var(--copper-hover))] transition rounded-full pl-4 pr-1 py-1 group">
                                            <span className="text-white text-sm font-medium">{t("home.hero.aside_cta_button", "Enviar mensagem WhatsApp")}</span>
                                            <span className="w-9 h-9 bg-white text-[hsl(var(--copper))] rounded-full flex items-center justify-center group-hover:translate-x-0.5 transition">
                                                <MessageCircle className="w-4 h-4" />
                                            </span>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-10 text-white/85 text-sm max-w-2xl">
                            <span className="text-[hsl(var(--copper))]">{t("home.hero.tagline_accent", "Bem feito. Ponto.")}</span> {t("home.hero.tagline_rest", "Sempre a par da sua obra.")}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
